import React from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Table,
  TableContainer,
  CircularProgress,
  Typography,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
  Divider
} from "@mui/material";
import CommonTableHeaders from "./TableHeaders/CommonTableHeaders";
import RequestsTableBody from "./TableBodys/RequestsTableBody";

interface GameInfo {
  id: string;
  [key: string]: any;
}

interface RequestsTableProps {
  gameInfo: GameInfo;
  setModalDets: (dets: any) => void;
}


const RequestsTable: React.FC<RequestsTableProps> = ({
  gameInfo,
  setModalDets,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const receivedRequests = useSelector(
    (state: any) => state.requests.receivedRequests,
  );

  const getNumGameReqs = () => {
    const reqIds = Object.keys(receivedRequests);
    const reqArr = reqIds
      .map((reqId) => receivedRequests[reqId])
      .filter((req) => req.gameId == gameInfo.id && req.status === "pending");
    return reqArr.length;
  };

  if (!receivedRequests) {
    return (
      <Card 
        elevation={2}
        sx={{ 
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: 200,
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress 
              color="primary" 
              size={40}
              thickness={4}
            />
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              Loading requests...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!getNumGameReqs()) {
    return (
      <Card 
        elevation={2}
        sx={{ 
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <CardContent>
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            px: 3,
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              No pending requests
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ opacity: 0.8 }}
            >
              New requests will appear here when players join your game
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={2}
      sx={{ 
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden'
      }}
    >
      <CardHeader
        title="Pending Requests"
        titleTypographyProps={{
          variant: 'h6',
          sx: { 
            fontWeight: 600,
            color: 'text.primary'
          }
        }}
        sx={{
          bgcolor: 'grey.50',
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2
        }}
      />
      <CardContent sx={{ p: 0 }}>
        <TableContainer
          sx={{
            maxHeight: isMobile ? 400 : 600,
            '& .MuiTableCell-root': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            }
          }}
        >
          <Table 
            stickyHeader 
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiTableHead-root': {
                '& .MuiTableCell-root': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 600,
                  borderBottom: 'none'
                }
              }
            }}
          >
            <CommonTableHeaders />
            <RequestsTableBody setModalDets={setModalDets} gameId={gameInfo.id} />
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default RequestsTable;
